import { Box, IconButton, Button, Input, Typography, useTheme, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { useEffect, useState } from "react";
import { DataGrid, GridToolbarQuickFilter } from "@mui/x-data-grid";
import { useAuth } from "../../../AuthContext";
import { tokens } from "../../../theme";
import { ToastContainer, toast } from "react-toastify";
import { Form } from "react-bootstrap";
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import AddIcon from '@mui/icons-material/Add';
import 'react-toastify/dist/ReactToastify.css';
import Nodata from '../../../assets/errors/nodata.png'; // Adjust the path as necessary

const Csr = () => {
  const [csr, setCsr] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCsr, setSelectedCsr] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [csrToDelete, setCsrToDelete] = useState(null);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { getData, addData, updateData, deleteData } = useAuth();

  // Form state
  const [formData, setFormData] = useState({
    csr_type: '',
    title: '',
    description: '',
    image: ''
  });

  useEffect(() => {
    async function fetchData() {
      try {
        const response = await getData('proccess.php', 'csr_V2'); // Fetch CSR data
        if (response.success) {
          setCsr(response.data);
        } else {
          toast.error(response.message || "Failed to load CSR data");
        }
      } catch (err) {
        toast.error("Network error: Failed to fetch CSR");
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [getData]);

  const handleAddClick = () => {
    setFormData({
      csr_type: '',
      title: '',
      description: '',
      image: ''
    });
    setOpenAddDialog(true);
  };

  const handleEditClick = (csrItem) => {
    setSelectedCsr(csrItem);
    setFormData({
      csr_type: csrItem.csr_type || '',
      title: csrItem.title || '',
      description: csrItem.description || '',
      image: csrItem.image || ''
    });
    setOpenEditDialog(true);
  };

  const handleDeleteClick = (csrItem) => {
    setCsrToDelete(csrItem);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await deleteData('proccess.php', 'csr_V2', csrToDelete.id); // Delete CSR by ID
      if (response.success) {
        toast.success(`CSR deleted successfully`);
        setCsr(csr.filter(item => item.id !== csrToDelete.id));
      } else {
        toast.error(response.message || "Failed to delete CSR");
      }
    } catch (err) {
      toast.error("Network error: Failed to delete CSR");
      console.error("Delete error:", err);
    }
    setOpenDeleteDialog(false);
    setCsrToDelete(null);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e) => {
    setFormData(prev => ({
      ...prev,
      image: e.target.files[0]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      let response;
      if (selectedCsr) {
        // Update existing CSR
        response = await updateData('proccess.php', 'csr_V2', formData, selectedCsr.id); // Update CSR
        if (response.success) {
          toast.success(`CSR updated successfully`);
          setCsr(response.data);
        }
      } else {
        // Add new CSR
        response = await addData('proccess.php', 'csr_V2', formData); // Add CSR
        if (response.success) {
          toast.success(`CSR added successfully`);
          setCsr(response.data); // Assuming response.data contains the updated CSR list
        }
      }

      if (!response.success) {
        toast.error(response.message || "Operation failed");
      }
    } catch (err) {
      toast.error("Network error: Operation failed");
      console.error("Operation error:", err);
    }
    setOpenEditDialog(false);
    setOpenAddDialog(false);
    setSelectedCsr(null);
  };

  const columns = [
    { field: "id", headerName: "ID", flex: 0.5, minWidth: 20 },
    { field: "csr_type", headerName: "CSR Type", flex: 1, minWidth: 120 },
    { field: "title", headerName: "CSR Title", flex: 1, minWidth: 120 },
    { field: "description", headerName: "Description", flex: 1, minWidth: 120 },
    {
      field: "image",
      headerName: "CSR Logo",
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Box width={50} height={50}>
          {params.value ? (
            <img
              src={`https://mindsinaction.com.na/assets/media/pictures/${params.value}`}
              alt={params.value || "CSR"}
              style={{ width: 50, height: 50, objectFit: "cover" }}
              onClick={() => window.open(`https://mindsinaction.com.na/assets/media/pictures/${params.value}`, "_blank")}
            />
          ) : (
            "No image"
          )}
        </Box>
      )
    },
    { field: "dateCreated", headerName: "Date Created", flex: 1, minWidth: 120 },
    {
      field: "actions",
      headerName: "Actions",
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap="8px">
          <IconButton
            onClick={() => handleEditClick(params.row)}
            sx={{ color: colors.blue[400] }}
          >
            <EditNoteOutlinedIcon />
          </IconButton>
          <IconButton
            onClick={() => handleDeleteClick(params.row)}
            sx={{ color: colors.orange[400] }}
          >
            <DeleteForeverOutlinedIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const NoDataOverlay = () => {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        height="100%"
        color={colors.dark[400]}
      >
        <img
          src={Nodata}
          alt="No data"
          style={{ width: 150, marginBottom: 16 }}
        />
        <Typography variant="h6">No CSR available</Typography>
      </Box>
    );
  };

  return (
    <Box m={"10px"}>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={openEditDialog || openAddDialog}
        onClose={() => {
          setOpenEditDialog(false);
          setOpenAddDialog(false);
          setSelectedCsr(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{selectedCsr ? "Edit CSR" : "Add New CSR"}</DialogTitle>
        <DialogContent>
          <Form onSubmit={handleSubmit} style={{ width: "100%", padding: "1rem" }}>
            <Box display={"flex"} gap={"1rem"} width={"100%"} mb={"1rem"} flexWrap="wrap">
              <TextField
                select
                style={{ width: "100%" }}
                id="csr_type"
                name="csr_type"
                label="CSR Type"
                variant="standard"
                value={formData.csr_type}
                onChange={handleFormChange}
                required
                SelectProps={{
                  native: true,
                }}
              >
                <option value=""></option>
                <option key='mia' value='mia'>MIA</option>
                <option key='partners' value='partners'>PARTNER</option>
              </TextField>
              <TextField
                style={{ width: "100%" }}
                id="title"
                name="title"
                label="CSR Title"
                variant="standard"
                value={formData.title}
                onChange={handleFormChange}
                required
              />
              <TextField
                style={{ width: "100%" }}
                id="description"
                name="description"
                label="Description"
                multiline
                minRows={4}
                maxRows={12}
                variant="standard"
                value={formData.description}
                onChange={handleFormChange}
                required
              />
              <Box width={"100%"}>
                <label>CSR Logo</label>
                <Input
                  type="file"
                  style={{ width: "100%" }}
                  id="image"
                  name="image"
                  onChange={handleFileChange}
                  required={!selectedCsr}
                />
              </Box>
            </Box>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenEditDialog(false);
              setOpenAddDialog(false);
              setSelectedCsr(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            sx={{ backgroundColor: colors.blue[400] }}
            variant="contained"
            endIcon={<SendIcon />}
          >
            {selectedCsr ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{csrToDelete?.title}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            sx={{ backgroundColor: colors.red[400] }}
            variant="contained"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Box mb={"30px"} ml={"15px"} mr={"15px"} display={"flex"} alignItems={"center"} justifyContent={"space-between"}>
        <Box flex={1}>
          <Typography
            variant="h4"
            color={colors.dark[400]}
            fontWeight={"bold"}
            sx={{ mb: "5px" }}
          >
            CSR Management
          </Typography>
          <Typography variant="h5" color={colors.blue[400]}>
            Manage CSR
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddClick}
          sx={{
            backgroundColor: colors.blue[400],
            '&:hover': {
              backgroundColor: colors.blue[500],
            }
          }}
        >
          Add CSR
        </Button>
      </Box>
      <Divider component="" />
      <Box sx={{ height: "calc(100vh - 250px)", width: "100%" }} backgroundColor={colors.dark[300]} padding={".2rem"} mt={"1rem"}>
        <DataGrid
          rows={csr}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          sx={{
            border: "none",
            ".MuiDataGrid-cell": {
              borderBottom: "none",
              backgroundColor: "none"
            },
            ".MuiDataGrid-toolbarContainer": {
              padding: "10px",
              backgroundColor: colors.primary[900],
              borderBottom: "none",
            },
            ".MuiDataGrid-quickFilter": {
              padding: "8px",
              borderRadius: "4px",
              border: `1px solid ${colors.primary[100]}`,
            },
            ".MuiDataGrid-columnHeaders": {
              backgroundColor: colors.primary[900],
            },
            ".MuiDataGrid-footerContainer": {
              backgroundColor: colors.primary[900],
            },
          }}
          slots={{
            toolbar: () => (
              <div>
                <GridToolbarQuickFilter />
              </div>
            ),
            noRowsOverlay: NoDataOverlay,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: {
                debounceMs: 500,
                placeholder: "Search...",
              },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default Csr;